// Manual trigger modal — the "New run" (implement) flow.
// Validates the issue key client-side, previews the Jira issue, and posts
// to POST /admin/v1/bss/runs.

import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../api";
import Icon from "./icons";
import { Modal, Spinner, apiErrorMessage } from "./shared";

const ISSUE_KEY_RE = /^[A-Z][A-Z0-9]+-\d+$/;
const BUDGETS = [2, 5, 10, 20];

const LaunchLabel = ({ submitting }) => {
  if (submitting)
    return (
      <>
        <Spinner /> Queueing…
      </>
    );
  return (
    <>
      <Icon.PlayCircle size={13} /> Launch run
    </>
  );
};

export default function NewRunModal({ open, initialMode, onClose, onLaunched }) {
  const [mode, setMode] = useState(initialMode || "implement");
  const [issueKey, setIssueKey] = useState("");
  const [budget, setBudget] = useState(5);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const lookupTimer = useRef(null);

  const valid = ISSUE_KEY_RE.test(issueKey.trim());

  useEffect(() => {
    if (!open) return;
    setMode(initialMode || "implement");
    setIssueKey("");
    setBudget(5);
    setPreview(null);
    setPreviewError(null);
    setSubmitError(null);
    setSubmitting(false);
    let cancelled = false;
    api
      .bssJiraSuggestions({ limit: 5 })
      .then((res) => {
        if (!cancelled) setSuggestions((res.data && res.data.items) || []);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, initialMode]);

  useEffect(() => {
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    setPreview(null);
    setPreviewError(null);
    if (!valid) return undefined;
    const key = issueKey.trim();
    setLoadingPreview(true);
    lookupTimer.current = setTimeout(() => {
      api
        .bssJiraIssue(key)
        .then((res) => {
          setPreview(res.data);
          setLoadingPreview(false);
        })
        .catch((err) => {
          setPreviewError(
            err && err.response && err.response.status === 404
              ? "Issue not found in Jira."
              : apiErrorMessage(err, "Could not reach Jira.")
          );
          setLoadingPreview(false);
        });
    }, 350);
    return () => {
      if (lookupTimer.current) clearTimeout(lookupTimer.current);
    };
  }, [issueKey, valid]);

  const launch = useCallback(() => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    const body = { issueKey: issueKey.trim(), mode };
    if (mode === "implement") body.budgetUsd = budget;
    api
      .bssCreateRun(body)
      .then((res) => {
        setSubmitting(false);
        onLaunched && onLaunched(res.data);
        onClose && onClose();
      })
      .catch((err) => {
        setSubmitting(false);
        setSubmitError(apiErrorMessage(err, "Could not launch run."));
      });
  }, [valid, submitting, issueKey, mode, budget, onLaunched, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Trigger a run"
      width={560}
      footer={
        <>
          <span
            style={{
              marginRight: "auto",
              fontSize: 11.5,
              color: "var(--bss-fg-subtle)"
            }}
          >
            Or move a Jira ticket to{" "}
            <span className="bss-code-inline">AUTO-SHIP</span> status — the agent
            picks it up automatically.
          </span>
          <button
            type="button"
            className="bss-btn bss-btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bss-btn bss-btn--primary"
            disabled={!valid || submitting}
            onClick={launch}
          >
            <LaunchLabel submitting={submitting} />
          </button>
        </>
      }
    >
      <div className="bss-form-row">
        <label className="bss-form-label" htmlFor="bss-issue-key">
          Jira issue key
        </label>
        <div style={{ position: "relative" }}>
          <Icon.Ticket
            size={14}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--bss-fg-subtle)"
            }}
          />
          <input
            id="bss-issue-key"
            className="bss-input"
            style={{
              width: "100%",
              paddingLeft: 32,
              fontFamily: "var(--bss-font-mono)"
            }}
            placeholder="SHIP-1234"
            value={issueKey}
            onChange={(e) => setIssueKey(e.target.value.toUpperCase())}
          />
          {issueKey && !valid && (
            <span
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--bss-danger)",
                fontSize: 11
              }}
            >
              Format: ABC-123
            </span>
          )}
          {valid && !loadingPreview && !previewError && (
            <Icon.Check
              size={14}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--bss-success)"
              }}
            />
          )}
          {loadingPreview && (
            <span
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)"
              }}
            >
              <Spinner size={12} />
            </span>
          )}
        </div>
        <div className="bss-form-hint">
          The agent will read this ticket from Jira and start the run.
        </div>
      </div>

      {!issueKey && suggestions.length > 0 && (
        <div className="bss-form-row">
          <label className="bss-form-label">Recent unscanned tickets</label>
          <div
            style={{ display: "flex", flexDirection: "column", gap: 4 }}
          >
            {suggestions.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setIssueKey(s.key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  background: "var(--bss-surface-soft)",
                  border: "1px solid var(--bss-border)",
                  borderRadius: "var(--bss-radius-md)",
                  cursor: "pointer",
                  textAlign: "left",
                  color: "var(--bss-fg)"
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--bss-font-mono)",
                    fontSize: 12,
                    color: "var(--bss-fg-muted)",
                    minWidth: 86
                  }}
                >
                  {s.key}
                </span>
                <span
                  style={{
                    fontSize: 12.5,
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {s.summary}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {previewError && valid && (
        <div
          className="bss-form-row"
          style={{ fontSize: 12.5, color: "var(--bss-danger)" }}
        >
          {previewError}
        </div>
      )}

      {preview && (
        <div
          className="bss-form-row"
          style={{
            background: "var(--bss-surface-soft)",
            border: "1px solid var(--bss-border)",
            borderRadius: "var(--bss-radius-md)",
            padding: 14
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <Icon.Ticket
              size={16}
              style={{ color: "var(--bss-fg-muted)", marginTop: 1 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: "var(--bss-font-mono)",
                  fontSize: 11.5,
                  color: "var(--bss-fg-muted)"
                }}
              >
                {preview.key}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--bss-fg-strong)",
                  fontWeight: 500,
                  marginTop: 2
                }}
              >
                {preview.summary}
              </div>
              {(preview.reporter || preview.issueType) && (
                <div
                  style={{
                    fontSize: 11.5,
                    color: "var(--bss-fg-muted)",
                    marginTop: 4
                  }}
                >
                  {[
                    preview.issueType,
                    preview.reporter && `Reporter: ${preview.reporter}`,
                    preview.priority
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {valid && mode === "implement" && (
        <div className="bss-form-row">
          <label className="bss-form-label">Budget cap</label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {BUDGETS.map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setBudget(b)}
                style={{
                  padding: "6px 12px",
                  background:
                    budget === b ? "var(--bss-boom-pink)" : "var(--bss-surface)",
                  color: budget === b ? "#fff" : "var(--bss-fg)",
                  border: `1px solid ${
                    budget === b
                      ? "var(--bss-boom-pink)"
                      : "var(--bss-border-strong)"
                  }`,
                  borderRadius: "var(--bss-radius-md)",
                  fontSize: 12.5,
                  fontFamily: "var(--bss-font-mono)",
                  cursor: "pointer"
                }}
              >
                ${b.toFixed(2)}
              </button>
            ))}
            <span
              style={{ fontSize: 11.5, color: "var(--bss-fg-subtle)" }}
            >
              Workspace default · <span className="bss-code-inline">$5</span>
            </span>
          </div>
        </div>
      )}

      {submitError && (
        <div
          className="bss-form-row"
          style={{ fontSize: 12.5, color: "var(--bss-danger)" }}
        >
          {submitError}
        </div>
      )}
    </Modal>
  );
}
